import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Package, Plus, Search, RefreshCw, Trash2, X, Save, ArrowUp, ArrowDown } from 'lucide-react';

function RedsHeavies() {
  const { API, user } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState([]);
  const [totals, setTotals] = useState({ totalStock: 0, totalDispatched: 0, totalAvailable: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Add Entry Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    scrapType: 'ZINC SCRAP',
    quantity: '',
    source: '',
    date: new Date().toISOString().split('T')[0],
    rate: '',
    amount: '',
    gstPercent: '18',
    totalValue: '',
    remarks: ''
  });

  const scrapTypes = ['ZINC SCRAP', 'COPPER SCRAP', 'BRASS SCRAP'];

  useEffect(() => {
    fetchStockData();
  }, [API]);

  const fetchStockData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/raw-material/reds-heavies-stock`);
      setStockData(response.data.summary || []);
      setTotals(response.data.totals || { totalStock: 0, totalDispatched: 0, totalAvailable: 0 });
    } catch (error) {
      console.error('Error fetching reds & heavies stock:', error);
      // Initialize with empty data for each type
      const defaultData = scrapTypes.map(type => ({
        id: type,
        scrapType: type,
        totalAdded: 0,
        totalDispatched: 0,
        available: 0,
        entries: 0
      }));
      setStockData(defaultData);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate amount when rate or quantity changes
      if (name === 'rate' || name === 'quantity') {
        const rate = name === 'rate' ? (parseFloat(value) || 0) : (parseFloat(prev.rate) || 0);
        const qty = name === 'quantity' ? (parseFloat(value) || 0) : (parseFloat(prev.quantity) || 0);
        if (rate > 0 && qty > 0) {
          const baseAmount = rate * qty;
          updated.amount = baseAmount.toFixed(2);
          const gst = parseFloat(prev.gstPercent) || 0;
          updated.totalValue = (baseAmount + (baseAmount * gst / 100)).toFixed(2);
        }
      }
      
      // Auto-calculate total value when amount or GST changes
      if (name === 'amount' || name === 'gstPercent') {
        const baseAmount = name === 'amount' ? (parseFloat(value) || 0) : (parseFloat(prev.amount) || 0);
        const gst = name === 'gstPercent' ? (parseFloat(value) || 0) : (parseFloat(prev.gstPercent) || 0);
        updated.totalValue = (baseAmount + (baseAmount * gst / 100)).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/raw-material/reds-heavies-stock`, {
        scrapType: formData.scrapType,
        quantity: parseFloat(formData.quantity),
        source: formData.source || 'Manual Entry',
        date: formData.date,
        rate: formData.rate ? parseFloat(formData.rate) : 0,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        gstPercent: formData.gstPercent ? parseFloat(formData.gstPercent) : 0,
        totalValue: formData.totalValue ? parseFloat(formData.totalValue) : 0,
        remarks: formData.remarks
      });
      
      setShowAddModal(false);
      setFormData({
        scrapType: 'ZINC SCRAP',
        quantity: '',
        source: '',
        date: new Date().toISOString().split('T')[0],
        rate: '',
        amount: '',
        gstPercent: '18',
        totalValue: '',
        remarks: ''
      });
      fetchStockData();
    } catch (error) {
      console.error('Error adding entry:', error);
      alert(error.response?.data?.detail || 'Error adding entry');
    }
    setSaving(false);
  };

  const getTypeColor = (type) => {
    if (type?.includes('ZINC')) return 'bg-slate-100 text-slate-700';
    if (type?.includes('COPPER')) return 'bg-orange-100 text-orange-700';
    if (type?.includes('BRASS')) return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getCardGradient = (type) => {
    if (type?.includes('ZINC')) return 'from-slate-500 to-slate-600';
    if (type?.includes('COPPER')) return 'from-orange-500 to-orange-600';
    if (type?.includes('BRASS')) return 'from-yellow-500 to-yellow-600';
    return 'from-gray-500 to-gray-600';
  };

  const filteredData = stockData.filter(item => 
    item.scrapType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="reds-heavies-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg">
            <Package className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Reds & Heavies Stock</h1>
            <p className="text-sm text-gray-500">Zinc, Copper & Brass Scrap Inventory</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-32"
            />
          </div>
          <button 
            onClick={fetchStockData}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg hover:from-red-600 hover:to-orange-600 transition text-sm font-medium"
          >
            <Plus size={16} />
            Add Stock Entry
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-slate-500 to-slate-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Zinc Scrap</p>
          <p className="text-2xl font-bold">
            {stockData.find(s => s.scrapType?.includes('ZINC'))?.available?.toLocaleString() || 0} kg
          </p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Copper Scrap</p>
          <p className="text-2xl font-bold">
            {stockData.find(s => s.scrapType?.includes('COPPER'))?.available?.toLocaleString() || 0} kg
          </p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Brass Scrap</p>
          <p className="text-2xl font-bold">
            {stockData.find(s => s.scrapType?.includes('BRASS'))?.available?.toLocaleString() || 0} kg
          </p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Available</p>
          <p className="text-2xl font-bold">{totals.totalAvailable?.toLocaleString() || 0} kg</p>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Scrap Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-green-600 uppercase">
                  <div className="flex items-center justify-end gap-1">
                    <ArrowUp size={14} />
                    Total Added (kg)
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-red-600 uppercase">
                  <div className="flex items-center justify-end gap-1">
                    <ArrowDown size={14} />
                    Dispatched (kg)
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-600 uppercase">Available Stock</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No stock data found. Add entries to start tracking.
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(item.scrapType)}`}>
                        {item.scrapType}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium text-green-600">+{(item.totalAdded || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium text-red-600">-{(item.totalDispatched || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-xl font-bold text-blue-600">{(item.available || 0).toLocaleString()} kg</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                        {item.entries || 0}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800">
        <h4 className="font-semibold mb-2">Stock Calculation:</h4>
        <p className="text-xs">
          <strong>Available Stock = Total Added - Dispatched</strong><br/>
          • <span className="text-green-600 font-medium">Added</span>: Stock entries added manually<br/>
          • <span className="text-red-600 font-medium">Dispatched</span>: Deducted when Zinc/Copper/Brass Scrap is sold via Dispatch Records
        </p>
      </div>

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add Stock Entry</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddEntry} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scrap Type *</label>
                <select
                  name="scrapType"
                  value={formData.scrapType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  required
                >
                  {scrapTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (kg) *</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  placeholder="e.g., 500"
                  step="any"
                  min="0"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate (₹/kg)</label>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    placeholder="e.g., 150"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
              </div>

              {/* Amount, GST & Total Value */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Amount (₹)</label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Before GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
                  <input
                    type="number"
                    name="gstPercent"
                    value={formData.gstPercent}
                    onChange={handleInputChange}
                    placeholder="18"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">Total Value (₹)</label>
                  <input
                    type="number"
                    name="totalValue"
                    value={formData.totalValue}
                    onChange={handleInputChange}
                    placeholder="With GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border-2 border-green-400 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-green-50 font-semibold text-green-700"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  placeholder="e.g., Production, Purchase, etc."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  placeholder="Optional notes..."
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-medium hover:from-red-600 hover:to-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><RefreshCw className="animate-spin" size={16} />Saving...</>
                  ) : (
                    <><Save size={16} />Add Entry</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RedsHeavies;
